import React, { useEffect, useMemo, useState } from 'react';
import {
  AddOn,
  Appointment,
  CertificateResultOutcome,
  CertificateSigner,
  MedicalCertificate,
  QuestionnaireSubmission,
  QuestionnaireTemplate,
  Service,
  User
} from '@/types';
import { Award, ClipboardCheck, FileText, Loader2, PenTool, TestTube, X } from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { api } from '@/services/api';
import { createId } from '@/lib/ids';
import { buildMapById } from '@/lib/collections';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { findSubmittedJobTitle } from '@/lib/submittedJobTitle';
import { getPendingQuestionnaireTemplateIds } from '@/lib/questionnaireRequirements';
import {
  buildCertificateResults,
  getExtraResultItems,
  getIssueCertificateWarnings,
  hasDrugAndAlcoholBooked
} from '@/lib/certificateResults';
import {
  certificateHasRestrictionOutcome,
  resolveCertificateRestrictions
} from '@/lib/certificateRenderer';

// Mirrors the technician portal's certificate outcomes (TechnicianView).
const CERTIFICATE_OUTCOMES = [
  { id: 'fit', label: 'Fit for Duties', active: 'bg-green-50 border-green-500 text-green-700' },
  {
    id: 'fit_restrictions',
    label: 'Fit with Restrictions',
    active: 'bg-yellow-50 border-yellow-500 text-yellow-700'
  },
  {
    id: 'referred',
    label: 'Referral Required',
    active: 'bg-blue-50 border-blue-500 text-blue-700'
  },
  { id: 'unfit', label: 'Unfit for Work', active: 'bg-red-50 border-red-500 text-red-700' }
] as const;

const DRUG_TEST_OPTIONS = [
  { id: '', label: 'Not Recorded' },
  { id: 'negative', label: 'Negative' },
  { id: 'non_negative', label: 'Non-negative' },
  { id: 'not_tested', label: 'Not Tested' }
] as const;

type CertDraft = Partial<
  Pick<MedicalCertificate, 'outcome' | 'drugTestOutcome' | 'advice' | 'restrictions' | 'jobTitle'>
>;

interface CertificateRecordModalProps {
  appointment: Appointment;
  /** null → issue a new certificate; set → amend this certificate. */
  certificate: MedicalCertificate | null;
  services: Service[];
  addOns: AddOn[];
  questionnaires: QuestionnaireTemplate[];
  submissions: QuestionnaireSubmission[];
  currentUser: User;
  onClose: () => void;
  onSaved: (certificate: MedicalCertificate) => void;
}

/**
 * Admin-portal counterpart of the technician certificate panel: record
 * results/outcomes and issue the certificate after the appointment (e.g. when
 * lab evidence arrives), or amend an issued one. Same field semantics and the
 * same staff-only endpoints as the technician flow.
 */
export const CertificateRecordModal: React.FC<CertificateRecordModalProps> = ({
  appointment,
  certificate,
  services,
  addOns,
  questionnaires,
  submissions,
  currentUser,
  onClose,
  onSaved
}) => {
  const isAmendingMode = Boolean(certificate);
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const questionnairesById = useMemo(() => buildMapById(questionnaires), [questionnaires]);

  const [certData, setCertData] = useState<CertDraft>(() =>
    certificate
      ? {
          outcome: certificate.outcome,
          drugTestOutcome: certificate.drugTestOutcome,
          advice: certificate.advice || '',
          restrictions: certificate.restrictions || '',
          jobTitle:
            certificate.jobTitle ||
            appointment.customerJobTitle ||
            findSubmittedJobTitle(submissions, questionnairesById)
        }
      : {
          outcome: 'fit',
          advice: '',
          restrictions: '',
          jobTitle:
            appointment.customerJobTitle || findSubmittedJobTitle(submissions, questionnairesById)
        }
  );
  const [extraResults, setExtraResults] = useState<Record<string, CertificateResultOutcome>>(() =>
    certificate
      ? Object.fromEntries(
          (certificate.results || []).map((row) => [`${row.kind}:${row.id}`, row.outcome])
        )
      : {}
  );
  // '' in amend mode keeps the technician already on the certificate.
  const [selectedSignerId, setSelectedSignerId] = useState<string>(
    certificate ? '' : currentUser.id
  );
  const [signers, setSigners] = useState<CertificateSigner[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getCertificateSigners()
      .then(({ signers: loaded }) => {
        if (!cancelled) setSigners(loaded);
      })
      .catch(() => {
        // Dropdown falls back to the logged-in user only.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const extraResultItems = getExtraResultItems({ appointment, servicesById, addOnsById });

  const buildResults = () =>
    buildCertificateResults({
      appointment,
      servicesById,
      addOnsById,
      primaryOutcome: certData.outcome || 'fit',
      drugTestOutcome: certData.drugTestOutcome,
      extraResults
    });

  const handleSubmit = async () => {
    const keepExistingSigner = isAmendingMode && !selectedSignerId;
    const signingAsSelf =
      !keepExistingSigner && (!selectedSignerId || selectedSignerId === currentUser.id);
    const selectedSigner: CertificateSigner | undefined = signingAsSelf
      ? { id: currentUser.id, name: currentUser.name, signature: currentUser.signature }
      : signers.find((s) => s.id === selectedSignerId);
    if (!keepExistingSigner && !selectedSigner?.signature) {
      showAlertModal(
        signingAsSelf
          ? 'You have no saved signature, so you cannot sign this certificate yourself. Select a signing technician with a saved signature instead.'
          : 'The selected technician has no saved signature. They need to save one from their own login before they can sign certificates.'
      );
      return;
    }

    if (!isAmendingMode) {
      const pendingTemplateNames = getPendingQuestionnaireTemplateIds(
        appointment,
        submissions,
        servicesById,
        addOnsById
      ).map((id) => questionnairesById.get(id)?.name || 'Questionnaire');
      const warnings = getIssueCertificateWarnings({
        pendingTemplateNames,
        submissionCount: submissions.length,
        drugAndAlcoholBooked: hasDrugAndAlcoholBooked({ appointment, servicesById, addOnsById }),
        drugTestOutcome: certData.drugTestOutcome
      });
      if (warnings.length > 0) {
        const confirmed = await showConfirmModal(
          `${warnings.join('\n\n')}\n\nAre you sure you want to issue the certificate?`,
          { title: 'Check Before Issuing', confirmText: 'Issue Anyway', isDestructive: false }
        );
        if (!confirmed) return;
      }
    }

    setIsSaving(true);

    // Restrictions only print alongside a "Fit with Restrictions" outcome
    // (conclusion or a per-test row); otherwise the text is dropped.
    const carriesRestrictions = certificateHasRestrictionOutcome({
      outcome: certData.outcome || 'fit',
      results: buildResults()
    });

    try {
      if (isAmendingMode && certificate) {
        const { certificate: amended } = await api.amendCertificate(certificate.id, {
          outcome: certData.outcome,
          // Explicit null clears a stored drug result ("Not Recorded").
          drugTestOutcome: certData.drugTestOutcome || null,
          advice: certData.advice,
          restrictions: carriesRestrictions ? certData.restrictions : '',
          jobTitle: (certData.jobTitle || '').trim(),
          ...(keepExistingSigner || !selectedSigner
            ? {}
            : {
                technicianName: selectedSigner.name,
                technicianSignature: selectedSigner.signature || undefined
              })
        });
        onSaved(amended);
        showAlertModal('Certificate amended.');
        return;
      }

      const today = new Date();
      const expiry = new Date();
      expiry.setFullYear(today.getFullYear() + 3);
      const newCertificate: MedicalCertificate = {
        id: createId().toUpperCase(),
        appointmentId: appointment.id,
        issuedAt: today.toISOString(),
        validUntil: expiry.toISOString(),
        technicianName: selectedSigner?.name ?? currentUser.name,
        technicianSignature: selectedSigner?.signature || currentUser.signature,
        outcome: certData.outcome || 'fit',
        drugTestOutcome: certData.drugTestOutcome || undefined,
        results: buildResults(),
        advice: certData.advice,
        restrictions: carriesRestrictions
          ? resolveCertificateRestrictions(appointment.healthAssessment, certData.restrictions)
          : undefined,
        jobTitle: (certData.jobTitle || '').trim() || undefined,
        templateId: 'default'
      };
      await api.issueCertificate(newCertificate);
      onSaved(newCertificate);
      showAlertModal('Certificate issued.');
    } catch (err: unknown) {
      showAlertModal(
        normalizeErrorMessage(
          err,
          isAmendingMode ? 'Error amending certificate.' : 'Error issuing certificate.'
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const willPrintRestrictions = certificateHasRestrictionOutcome({
    outcome: certData.outcome || 'fit',
    results: buildResults()
  });
  const healthAssessmentClauses =
    !isAmendingMode && appointment.healthAssessment?.fitStatus === 'fit_restrictions'
      ? (appointment.healthAssessment.restrictions || []).map((r) => r.trim()).filter(Boolean)
      : [];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg text-slate-900 uppercase tracking-tight">
              {isAmendingMode ? 'Amend Certificate' : 'Issue Certificate'}
            </h3>
            <p className="text-[10px] text-slate-400 font-black uppercase">
              {appointment.customerName} | Ref: {appointment.id}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto">
          <div>
            <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-teal-600" /> Clinical Conclusion
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CERTIFICATE_OUTCOMES.map((opt) => (
                <label
                  key={opt.id}
                  className={`border-2 rounded-2xl p-3 cursor-pointer transition-all flex items-center justify-center text-center ${
                    certData.outcome === opt.id
                      ? `${opt.active} ring-4 ring-slate-900/5`
                      : 'border-slate-100 bg-white text-slate-500 hover:border-teal-300'
                  }`}
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={certData.outcome === opt.id}
                    onChange={() => setCertData({ ...certData, outcome: opt.id })}
                  />
                  <span className="font-black text-[10px] uppercase tracking-widest">
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {extraResultItems.length > 0 && (
            <div>
              <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-teal-600" /> Individual Test Results
              </h4>
              <p className="text-[10px] text-slate-500 mb-3">
                {isAmendingMode
                  ? 'An amendment can change the clinical conclusion and drug test result above/below; the other per-test rows are shown for reference and keep their issued values.'
                  : 'A result per additional service/test — each prints as its own row on the certificate. Drug & Alcohol results come from the Drug Test Result picker below.'}
              </p>
              <div className="space-y-2">
                {extraResultItems.map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center justify-between gap-3 border-2 border-slate-100 rounded-2xl p-3 bg-slate-50"
                  >
                    <span className="text-xs font-bold text-slate-700">{item.name}</span>
                    <select
                      className="border-2 border-slate-100 rounded-xl p-2 text-xs font-bold bg-white outline-none focus:border-teal-400 disabled:opacity-60 disabled:cursor-not-allowed"
                      value={extraResults[item.key] || 'fit'}
                      // The amend endpoint deliberately accepts no results array —
                      // only the conclusion and drug rows are re-synced server-side.
                      disabled={isAmendingMode}
                      onChange={(e) =>
                        setExtraResults((prev) => ({
                          ...prev,
                          [item.key]: e.target.value as CertificateResultOutcome
                        }))
                      }
                    >
                      <option value="fit">Fit</option>
                      <option value="fit_restrictions">Fit with Restriction</option>
                      <option value="referred">Referred to OHP</option>
                      <option value="unfit">Unfit</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
              <TestTube className="w-4 h-4 text-teal-600" /> Drug Test Result
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DRUG_TEST_OPTIONS.map((option) => {
                const isActive = String(certData.drugTestOutcome || '') === option.id;
                return (
                  <label
                    key={option.id || 'empty'}
                    className={`border rounded-2xl p-3 cursor-pointer transition-all flex items-center justify-center text-center ${
                      isActive
                        ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-100'
                        : 'border-slate-200 bg-white hover:border-teal-300'
                    }`}
                  >
                    <input
                      type="radio"
                      className="hidden"
                      checked={isActive}
                      onChange={() =>
                        setCertData({
                          ...certData,
                          drugTestOutcome:
                            (option.id as MedicalCertificate['drugTestOutcome']) || undefined
                        })
                      }
                    />
                    <span
                      className={`font-black text-[10px] uppercase tracking-widest ${
                        isActive ? 'text-teal-700' : 'text-slate-500'
                      }`}
                    >
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="mt-2 text-[10px] text-slate-500">
              Optional. Leave as "Not Recorded" to store no drug test result.
            </p>
          </div>

          <div>
            <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-600" /> Restrictions
            </h4>
            {!willPrintRestrictions ? (
              <p className="border-2 border-slate-100 bg-slate-50 rounded-2xl p-4 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                The conclusion is not "Fit with Restriction" — no restrictions are printed on this
                certificate.
              </p>
            ) : (
              <>
                {healthAssessmentClauses.length > 0 && (
                  <div className="mb-3 border-2 border-yellow-200 bg-yellow-50 rounded-2xl p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-yellow-700 mb-2">
                      From Health Assessment — these will appear on the certificate
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      {healthAssessmentClauses.map((clause) => (
                        <li key={clause} className="text-xs font-medium text-slate-700">
                          {clause}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <textarea
                  className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm h-24 focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
                  placeholder="Specific work restrictions for employer (e.g. no heavy lifting, avoid heights)..."
                  value={certData.restrictions || ''}
                  onChange={(e) => setCertData({ ...certData, restrictions: e.target.value })}
                />
              </>
            )}
          </div>

          <div>
            <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-600" /> Clinical Advice
            </h4>
            <textarea
              className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm h-24 focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
              placeholder="General clinical notes for employer and patient..."
              value={certData.advice || ''}
              onChange={(e) => setCertData({ ...certData, advice: e.target.value })}
            />
          </div>

          <div>
            <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-3">
              Job Title
            </h4>
            <input
              className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
              placeholder="e.g. Forklift Operator"
              value={certData.jobTitle || ''}
              onChange={(e) => setCertData({ ...certData, jobTitle: e.target.value })}
            />
            <p className="text-[10px] text-slate-400 mt-2 font-medium">
              Printed on the certificate.
            </p>
          </div>

          <div>
            <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
              <PenTool className="w-4 h-4 text-teal-600" /> Signing Technician
            </h4>
            <select
              className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
              value={selectedSignerId}
              onChange={(e) => setSelectedSignerId(e.target.value)}
            >
              {isAmendingMode && (
                <option value="">Keep current — {certificate?.technicianName || 'unknown'}</option>
              )}
              {!signers.some((s) => s.id === currentUser.id) && (
                <option value={currentUser.id}>{currentUser.name} (me)</option>
              )}
              {signers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.id === currentUser.id ? ' (me)' : ''}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400 mt-2 font-medium">
              The selected technician's name and saved signature are printed at the bottom of the
              certificate. Only technicians with a saved signature are listed.
            </p>
          </div>
        </div>

        <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-3 text-slate-400 font-bold uppercase text-[10px] tracking-widest"
          >
            Discard
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-teal-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50 shadow-xl shadow-teal-600/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Award className="w-4 h-4" />
            )}{' '}
            {isAmendingMode ? 'Save Amendment' : 'Generate Certificate'}
          </button>
        </div>
      </div>
    </div>
  );
};
